/**
 * Pure tax-scheme and §14-completeness functions for the `expenses` feature (M4a).
 *
 * ALL functions are pure (no side effects, no network calls, no Firebase imports).
 * They can be unit-tested directly against the 8 Soll-Paare from plan-feedback-solar-m4.md.
 *
 * Key design decisions (all BLOCKING from [E-RECHNUNG-EXPERTE] gate):
 *
 *   V-1 — §14 Nr. 6 Leistungszeitpunkt (BT-72/73/74) is checked; its absence causes
 *          completenessStatus='incomplete' and vatDeductible=false.
 *
 *   V-2 — §33 UStDV Kleinbetrag-Lockerung is EXCLUDED for §13b / IGL / Fernverkauf
 *          (§33 S. 2 UStDV). A §13b invoice ≤ 250 € still requires the full §14 check.
 *
 *   V-3 — import_third_country: vatOwedCents=0, vatDeductibleCents=0, vatDeductible=false.
 *          EUSt is claimed via a separate customs document (§15 Abs. 1 Nr. 2 UStG).
 *
 *   V-4 — §13b/IGL vatOwedCents is SELF-COMPUTED (netCents × rate/100, default 19 %).
 *          vatDeductibleCents = vatOwedCents ONLY when userIsFullyVatDeductible=true.
 *          For §19-Kleinunternehmer: vatOwedCents > 0 (owed!), vatDeductibleCents = 0.
 *
 *   V-5 — Default for any unrecognised/missing/mixed categoryCode → 'needs_review'.
 *          NEVER default to 'standard'.
 *
 *   BT-48 Parser Gap — IGL (K) without a parsed buyerVatId (BT-48) cannot be verified
 *          for §14a Abs. 3 completeness automatically. Conservative decision: mark as
 *          'needs_review' (not 'complete') and add 'igl_ustid_kaeufer_nicht_pruefbar'
 *          to missingMandatoryFields, so the user must confirm. Extending the ZUGFeRD
 *          parser to extract BT-48 is tracked as a separate follow-up ticket.
 *
 * @module expenseTax
 */

import { eurosToCents } from './moneyUtils';
import {
  TaxScheme,
  ExpenseDraft,
  ExpenseVatBreakdownEntry,
  CompletenessCheckResult,
} from '../types/expenseTypes';
import { ParsedZugferdData } from '../types/receivedInvoiceTypes';

// ─── SellerData subset ────────────────────────────────────────────────────────

/**
 * Minimal seller-data shape required by `mapReceivedInvoiceToExpenseDraft`.
 * Sourced from AuthContext `sellerData` at the call site.
 */
export interface ExpenseSellerContext {
  /**
   * Whether the user is a §19 Kleinunternehmer.
   * When true, the user owes §13b/IGL VAT but CANNOT deduct it.
   */
  isSmallBusiness?: boolean;
}

// ─── TaxCompletenessInput (K-2/A reuse surface) ───────────────────────────────

/**
 * The minimal slice of `ParsedZugferdData` that `deriveTaxScheme` and
 * `checkInputInvoiceCompleteness` actually read.
 *
 * It is declared as a `Pick` of `ParsedZugferdData`, so the full parsed type is
 * structurally assignable to it — the existing caller
 * `mapReceivedInvoiceToExpenseDraft` keeps passing a complete `ParsedZugferdData`
 * without any cast. A future receipt-scan adapter (M13a-4) that can only supply
 * these fields may call both functions directly against this narrower contract.
 */
export type TaxCompletenessInput = Pick<
  ParsedZugferdData,
  | 'documentTypeCode'
  | 'documentNo'
  | 'documentDate'
  | 'grandTotal'
  | 'netTotal'
  | 'sellerName'
  | 'sellerStreet'
  | 'sellerZip'
  | 'sellerCity'
  | 'sellerTaxId'
  | 'sellerVatId'
  | 'buyerName'
  | 'buyerStreet'
  | 'buyerZip'
  | 'buyerCity'
  | 'documentNotes'
  | 'taxBreakdown'
  | 'lineItems'
  | 'deliveryDate'
  | 'billingPeriodStart'
  | 'referencedInvoices'
>;

// ─── Internal helpers ─────────────────────────────────────────────────────────

/**
 * Returns true when a string is non-empty after trimming.
 */
function nonEmpty(value: string | null | undefined): boolean {
  return typeof value === 'string' && value.trim().length > 0;
}

/**
 * Returns true when an address trio (street, zip, city) is all non-empty.
 */
function hasAddress(
  street: string | null | undefined,
  zip: string | null | undefined,
  city: string | null | undefined,
): boolean {
  return nonEmpty(street) && nonEmpty(zip) && nonEmpty(city);
}

// ─── Belegart (UNTDID 1001 / BT-3) classification ─────────────────────────────

/**
 * Returns true for an incoming Gutschrift / credit note (UNTDID 1001 code 381).
 *
 * A 381 is a Storno-/Korrektur-Gutschrift (kaufmännische Gutschrift) — the
 * reduction of an incoming invoice (§17 Abs. 1 UStG Vorsteuer-Korrektur). It is
 * booked as a NEGATIVE expense (signierte Cents).
 *
 * NOTE: This is NOT the §14 Abs. 2 S. 2 UStG self-billing "Gutschrift" (code 389),
 * which is itself a *Rechnung* (positive), not a negative document.
 */
function isCreditNote(parsed: Pick<ParsedZugferdData, 'documentTypeCode'>): boolean {
  return (parsed.documentTypeCode ?? '').trim() === '381';
}

/**
 * Fail-closed allowlist gate for the BT-3 document type code.
 *
 * Only 381 is auto-processed as a credit (negative). Every other code that is
 * either a credit-note variant or carries contradictory negative totals must be
 * routed to `needs_review` so the user confirms the booking direction — NEVER
 * silently booked with a wrong sign.
 *
 * Returns true when the document requires manual review based on its type code:
 *   - 380 (commercial invoice) with NEGATIVE totals  → "negative Rechnung" (untypisch)
 *   - 384 (corrected invoice)                         → semantics ambiguous (Voll vs. Delta)
 *   - 261 / 396 (self-billed / factored credit note)  → credit variants, review by default
 *   - unknown/empty code with NEGATIVE totals         → never silently negate
 *
 * 381 returns false here — it is handled as a credit (see isCreditNote).
 *
 * @param parsed - The parsed ZUGFeRD document.
 */
function requiresDocumentTypeReview(parsed: ParsedZugferdData): boolean {
  const code = (parsed.documentTypeCode ?? '').trim();
  const netTotal = parsed.netTotal ?? 0;

  // 381 is the only code auto-handled as a credit — not a review case.
  if (code === '381') {
    return false;
  }

  // 384 (Rechnungskorrektur): full re-statement vs. delta is not auto-derivable.
  if (code === '384') {
    return true;
  }

  // 261 / 396: self-billed / factored credit note — review by default (rare).
  if (code === '261' || code === '396') {
    return true;
  }

  // 380 or unknown/empty code with negative totals → "negative invoice" variant:
  // do not silently negate; the user must confirm whether a credit is meant.
  if (netTotal < 0) {
    return true;
  }

  return false;
}

// ─── deriveTaxScheme ──────────────────────────────────────────────────────────

/**
 * Derives the tax scheme from the EN-16931 BT-118 category codes present in
 * the `taxBreakdown` array of a parsed ZUGFeRD document.
 *
 * Mapping (authoritative table from plan-feedback-solar-m4.md §A):
 *   S  → standard
 *   AE → reverse_charge_13b  (§13b — recipient owes VAT)
 *   K  → intra_community_6a  (innergem. Erwerb §1a / §13a Abs. 1 Nr. 2)
 *   Z  → zero_exempt         (zero-rated)
 *   E  → zero_exempt         (tax-exempt §4) — or small_business_19 heuristic; both vatDeductible=false
 *   G  → needs_review        (free export — may be inbound import, ambiguous)
 *   O  → needs_review        (not subject to tax — hidden §13b risk, A-2)
 *   L/M → needs_review       (IGIC/IPSI Sonderfälle)
 *   missing/mixed → needs_review  (fail-closed, V-5)
 *
 * FAIL-CLOSED: any ambiguity, missing code, or mixed contradictory codes → needs_review.
 * NEVER returns 'standard' as a default.
 *
 * @param parsed - The parsed ZUGFeRD document.
 * @returns The derived TaxScheme value.
 */
export function deriveTaxScheme(parsed: TaxCompletenessInput): TaxScheme {
  const breakdown = parsed.taxBreakdown ?? [];

  // Collect non-empty, distinct category codes
  const codes = new Set(
    breakdown
      .map((b) => (b.categoryCode ?? '').trim().toUpperCase())
      .filter((c) => c.length > 0),
  );

  // No codes at all → fail-closed (V-5)
  if (codes.size === 0) {
    return 'needs_review';
  }

  // Check for priority codes — these take precedence and require no mixing ambiguity

  // §13b Reverse Charge
  if (codes.has('AE')) {
    // AE mixed with other codes is contradictory → needs_review
    if (codes.size > 1) {
      return 'needs_review';
    }
    return 'reverse_charge_13b';
  }

  // Intra-community acquisition (Empfängersicht: §1a / §13a Abs. 1 Nr. 2 / §15 Abs. 1 Nr. 3)
  // NOTE: label 'intra_community_6a' retained per architecture spec (§6a is the
  // supplier-side exemption; code comments clarify the recipient-side §§).
  if (codes.has('K')) {
    // K mixed with other codes → needs_review
    if (codes.size > 1) {
      return 'needs_review';
    }
    return 'intra_community_6a';
  }

  // O = not subject to tax — MUST NOT default to zero/import.
  // Hidden §13b trap: a foreign supplier outside scope may actually trigger
  // Empfängerschuldnerschaft. (A-2 from plan-feedback-solar-m4.md)
  if (codes.has('O')) {
    return 'needs_review';
  }

  // G = free export item / Ausfuhr — ambiguous from recipient perspective.
  // Could be an inbound import from our side. Requires user clarification.
  if (codes.has('G')) {
    return 'needs_review';
  }

  // L/M = IGIC (Canary Islands) / IPSI (Ceuta/Melilla) — special jurisdiction codes.
  if (codes.has('L') || codes.has('M')) {
    return 'needs_review';
  }

  // Standard rate — only when all codes are S
  if (codes.has('S')) {
    // Mixed S with other codes → needs_review (contradictory)
    const nonS = new Set(Array.from(codes).filter((c) => c !== 'S'));
    if (nonS.size > 0) {
      return 'needs_review';
    }
    return 'standard';
  }

  // Zero-rated
  if (codes.has('Z')) {
    const nonZ = new Set(Array.from(codes).filter((c) => c !== 'Z'));
    if (nonZ.size > 0) {
      return 'needs_review';
    }
    return 'zero_exempt';
  }

  // E = tax-exempt (§4) or §19-Kleinunternehmer heuristic.
  // Both zero_exempt and small_business_19 result in vatDeductible=false, so the
  // distinction is display-only (A-3). Return zero_exempt as the safe default
  // for E; the UI may refine to small_business_19 based on exemptionReason (BT-120).
  if (codes.has('E')) {
    const nonE = new Set(Array.from(codes).filter((c) => c !== 'E'));
    if (nonE.size > 0) {
      return 'needs_review';
    }
    return 'zero_exempt';
  }

  // Anything else (unknown codes) → fail-closed (V-5)
  return 'needs_review';
}

// ─── checkInputInvoiceCompleteness ────────────────────────────────────────────

/**
 * Checks whether a parsed ZUGFeRD invoice satisfies the §14 Abs. 4 UStG
 * mandatory-field requirements (and the §33 UStDV small-amount relaxation
 * where applicable).
 *
 * §33 UStDV relaxation (≤ 250 € gross) reduces mandatory fields, BUT:
 *   §33 Satz 2 UStDV explicitly EXCLUDES §13b (reverse_charge_13b) and
 *   intra-community acquisitions (intra_community_6a) — those always require
 *   the full §14 checklist. (V-2)
 *
 * BT-48 Parser Gap: when taxScheme === 'intra_community_6a', the IGL check
 *   for the buyer's VAT ID (§14a Abs. 3 — "USt-IdNr beider Parteien") cannot
 *   be satisfied automatically because the ZUGFeRD parser does not yet extract
 *   BT-48 (buyer VAT ID). Conservative decision: return completenessStatus=
 *   'needs_review' and add 'igl_ustid_kaeufer_nicht_pruefbar' to the missing
 *   list. The user must confirm. Parser extension is a follow-up ticket.
 *
 * @param parsed - The parsed ZUGFeRD document.
 * @param taxScheme - The pre-derived tax scheme (run deriveTaxScheme first).
 * @returns CompletenessCheckResult with status and list of missing fields.
 */
export function checkInputInvoiceCompleteness(
  parsed: TaxCompletenessInput,
  taxScheme: TaxScheme,
): CompletenessCheckResult {
  const missing: string[] = [];

  const isCredit = isCreditNote(parsed);

  // §33 UStDV: small-amount relaxation applies ONLY when:
  //   a) |grandTotal| ≤ 250 €, AND
  //   b) taxScheme is NOT §13b or IGL (§33 S. 2 UStDV excludes them) (V-2)
  // Absolute value: a Gutschrift (381) carries a negative grandTotal; the §33
  // threshold is about the absolute amount, so a −1 190 € credit note is NOT a
  // Kleinbetrag and must still pass the full §14 checklist.
  const isSmallAmount =
    Math.abs(parsed.grandTotal ?? 0) <= 250 &&
    taxScheme !== 'reverse_charge_13b' &&
    taxScheme !== 'intra_community_6a';

  // ── Always required (even for Kleinbetrag §33) ─────────────────────────────

  // §14 Abs. 4 Nr. 1a — name + address of the supplier
  if (!nonEmpty(parsed.sellerName) || !hasAddress(parsed.sellerStreet, parsed.sellerZip, parsed.sellerCity)) {
    missing.push('leistender_name_anschrift');
  }

  // §14 Abs. 4 Nr. 3 — invoice date
  if (!nonEmpty(parsed.documentDate)) {
    missing.push('ausstellungsdatum');
  }

  // §14 Abs. 4 Nr. 5 — quantity and nature of the supply
  // Satisfied by lineItems[] OR descriptive documentNotes[]
  const hasLineItems = Array.isArray(parsed.lineItems) && parsed.lineItems.length > 0;
  const hasDocNotes = Array.isArray(parsed.documentNotes) && parsed.documentNotes.some(nonEmpty);
  if (!hasLineItems && !hasDocNotes) {
    missing.push('menge_art_leistung');
  }

  // §14 Abs. 4 Nr. 8 — VAT rate + amount, or exemption/RC reason
  const hasTaxBreakdown = Array.isArray(parsed.taxBreakdown) && parsed.taxBreakdown.length > 0;
  if (!hasTaxBreakdown) {
    missing.push('steuersatz_betrag_oder_befreiungshinweis');
  }

  // ── Regelrechnung only (not required for §33 Kleinbetrag) ─────────────────

  if (!isSmallAmount) {
    // §14 Abs. 4 Nr. 1b — name + address of the recipient
    if (!nonEmpty(parsed.buyerName) || !hasAddress(parsed.buyerStreet, parsed.buyerZip, parsed.buyerCity)) {
      missing.push('empfaenger_name_anschrift');
    }

    // §14 Abs. 4 Nr. 2 — tax number OR VAT ID of the supplier (at least one)
    if (!nonEmpty(parsed.sellerTaxId) && !nonEmpty(parsed.sellerVatId)) {
      missing.push('steuernummer_oder_ustid');
    }

    // §14 Abs. 4 Nr. 4 — sequential invoice number
    if (!nonEmpty(parsed.documentNo)) {
      missing.push('rechnungsnummer');
    }

    // §14 Abs. 4 Nr. 6 — service date / delivery date / billing period (BT-72/73/74)
    // (V-1: most common reason for denial of input tax deduction)
    // Exception: if deliveryDate === documentDate AND an explicit note states so
    // (BMF v. 16.10.2009) — we cannot reliably detect the note, so we require
    // explicit presence of deliveryDate or billingPeriodStart to be safe.
    const hasServiceDate =
      nonEmpty(parsed.deliveryDate ?? undefined) ||
      nonEmpty(parsed.billingPeriodStart ?? undefined);
    if (!hasServiceDate) {
      missing.push('leistungszeitpunkt');
    }

    // §14 Abs. 4 Nr. 7 — net amount (Entgelt)
    // For a Gutschrift (381) the Entgelt is a Minderungsbetrag that may be
    // negative (or positive in the EN-canonical representation); it is present
    // as long as it is non-zero. For a normal invoice the net must be > 0.
    const entgeltPresent = isCredit
      ? (parsed.netTotal ?? 0) !== 0
      : (parsed.netTotal ?? 0) > 0;
    if (!entgeltPresent) {
      missing.push('entgelt');
    }
  }

  // ── §14a special requirements (always, regardless of small-amount) ─────────

  // §14a Abs. 5 — §13b Reverse Charge: document must carry the RC notice
  // (BT-120 exemptionReason or BT-22 documentNotes)
  if (taxScheme === 'reverse_charge_13b') {
    // §14a Abs. 5 requires a phrase indicating the recipient owes the VAT.
    // Match only whole legal phrases / unambiguous tokens — NOT bare two-letter
    // substrings like 'rc' which is a substring of common German words (e.g.
    // 'durch', 'Vorschrift') and would cause a false-positive (F-1 fail-open fix).
    const rcPhrases = [
      'steuerschuldnerschaft',
      'steuerschuldner',
      '§13b',
      '§ 13b',
      '13b ustg',
      'reverse charge',
    ];
    const exemptionText = (parsed.taxBreakdown ?? [])
      .map((b) => (b.exemptionReason ?? '').toLowerCase())
      .join(' ');
    const notesText = (parsed.documentNotes ?? []).join(' ').toLowerCase();
    const hasRcHinweis = rcPhrases.some(
      (phrase) => exemptionText.includes(phrase) || notesText.includes(phrase),
    );
    if (!hasRcHinweis) {
      missing.push('reverse_charge_hinweis');
    }
  }

  // §14a Abs. 3 — IGL: VAT IDs of BOTH parties required
  if (taxScheme === 'intra_community_6a') {
    // Seller VAT ID check
    if (!nonEmpty(parsed.sellerVatId)) {
      missing.push('igl_ustid_lieferant');
    }
    // BT-48 Parser Gap: buyer VAT ID (BT-48) is not extracted by the current
    // ZUGFeRD parser. Conservative: flag as not checkable → needs user confirmation.
    // This is a known limitation documented in architecture-m4.md §6.
    // Do NOT mark 'complete' when we cannot verify §14a Abs. 3 buyer-side requirement.
    missing.push('igl_ustid_kaeufer_nicht_pruefbar');
  }

  // ── §17 / GoBD: Gutschrift (381) Vorgänger-Bezug (BT-25 / BG-3) ────────────
  // EN 16931 does not hard-require BT-25 on a 381, but §17 + GoBD-Belegfunktion
  // need the reference to the original invoice so the Vorsteuer-Korrektur is
  // auditable. Missing reference is NOT a hard block: the user can supply it in
  // the confirmation modal. Treated as a soft review marker → needs_review.
  if (isCredit) {
    const hasReference =
      Array.isArray(parsed.referencedInvoices) && parsed.referencedInvoices.length > 0;
    if (!hasReference) {
      missing.push('gutschrift_bezug_fehlt');
    }
  }

  // ── Determine completenessStatus ───────────────────────────────────────────

  // Soft review markers: present fields we cannot auto-verify, but the document
  // may still be materially correct. They yield needs_review (not 'incomplete')
  // when no genuine hard-missing field is also present.
  //   - igl_ustid_kaeufer_nicht_pruefbar — §14a Abs. 3 buyer VAT ID (BT-48 parser gap)
  //   - gutschrift_bezug_fehlt           — §17 Vorgänger-Bezug for a 381 Gutschrift
  const SOFT_REVIEW_MARKERS = ['igl_ustid_kaeufer_nicht_pruefbar', 'gutschrift_bezug_fehlt'];
  const hasSoftReview = missing.some((m) => SOFT_REVIEW_MARKERS.includes(m));
  const hasHardMissing = missing.some((m) => !SOFT_REVIEW_MARKERS.includes(m));

  let completenessStatus: 'complete' | 'incomplete' | 'needs_review';
  if (missing.length === 0) {
    completenessStatus = 'complete';
  } else if (hasSoftReview && !hasHardMissing) {
    // Only soft review markers — everything else is present, but a field needs
    // user confirmation (IGL buyer VAT ID, or Gutschrift original-invoice ref).
    completenessStatus = 'needs_review';
  } else {
    // Genuine missing mandatory fields
    completenessStatus = 'incomplete';
  }

  return { completenessStatus, missingMandatoryFields: missing };
}

// ─── mapReceivedInvoiceToExpenseDraft ─────────────────────────────────────────

/**
 * Maps a parsed ZUGFeRD incoming invoice to an ExpenseDraft.
 *
 * This is a PURE function — no network calls, no side effects. The draft is
 * always returned with status='draft' and taxSchemeConfirmedByUser=false.
 * A human must always confirm before the expense is booked.
 *
 * Amount handling:
 *   - All floats from ParsedZugferdData are converted to integer cents via eurosToCents().
 *   - For standard: vatOwedCents = eurosToCents(taxTotal).
 *   - For §13b/IGL: vatOwedCents = round(netCents × 19/100) — SELF-COMPUTED (V-4).
 *     Default rate 19 %; override to 7 % is done by the user in the confirmation modal (B-2).
 *   - For import_third_country: vatOwedCents = 0, vatDeductibleCents = 0 (V-3).
 *   - For all others: vatOwedCents = 0.
 *
 * vatDeductible derivation (fail-closed, plan-feedback-solar-m4.md §C-3):
 *   true ONLY when ALL hold:
 *     (1) completenessStatus === 'complete'
 *     (2) taxScheme ∈ {standard, reverse_charge_13b, intra_community_6a}
 *     (3) standard → vatOwedCents !== 0 (a 381 Gutschrift has vatOwedCents < 0;
 *         §17 Abs. 1 UStG requires the Vorsteuer-Korrektur to be deductible-eligible)
 *     (4) §13b/IGL → sellerContext.isSmallBusiness === false (user is fully deductible)
 *
 * Gutschrift (BT-3 code 381): booked as a NEGATIVE expense — netCents/grossCents/
 * vatOwedCents/vatDeductibleCents all carry a negative sign (signierte Cents).
 * The sign is forced via -Math.abs(...) regardless of the source sign, because an
 * EN-canonical 381 carries positive totals while some parsers emit negatives.
 * Other ambiguous codes (384, 261/396, 380/unknown with negative totals) → needs_review.
 *
 * @param parsed - Parsed ZUGFeRD document from `received_invoices`.
 * @param sellerContext - Minimal seller-data for deductibility checks.
 * @returns ExpenseDraft ready for the confirmation modal.
 */
export function mapReceivedInvoiceToExpenseDraft(
  parsed: ParsedZugferdData,
  sellerContext: ExpenseSellerContext,
): ExpenseDraft {
  // ── Belegart weiche (BT-3 / UNTDID 1001) ────────────────────────────────────
  // 381 → Gutschrift: book as a NEGATIVE expense (§17 UStG Vorsteuer-Korrektur).
  // Other credit-note variants / negative totals on unexpected codes → needs_review
  // (fail-closed: never auto-book with a guessed sign).
  const isCredit = isCreditNote(parsed);

  // ── Cent conversion ─────────────────────────────────────────────────────────
  // Sign normalisation (Frage 4): an EN-canonical 381 carries POSITIVE totals
  // (the type code carries the direction), but some suppliers/parsers emit
  // negative totals. The source sign is therefore ambiguous — for a 381 we ALWAYS
  // force the stored cents negative via -Math.abs(...), independent of the source.
  const netCents = isCredit
    ? -Math.abs(eurosToCents(parsed.netTotal ?? 0))
    : eurosToCents(parsed.netTotal ?? 0);
  const grossCents = isCredit
    ? -Math.abs(eurosToCents(parsed.grandTotal ?? 0))
    : eurosToCents(parsed.grandTotal ?? 0);

  // ── Tax scheme ──────────────────────────────────────────────────────────────
  // Document-type review cases (384, 261/396, 380-with-negative-totals, unknown
  // code with negatives) are forced to needs_review so they are never auto-booked.
  const taxScheme: TaxScheme = requiresDocumentTypeReview(parsed)
    ? 'needs_review'
    : deriveTaxScheme(parsed);

  // ── vatOwedCents (ALWAYS declared — Deklarationspflicht) ────────────────────
  let vatOwedCents: number;

  if (taxScheme === 'reverse_charge_13b' || taxScheme === 'intra_community_6a') {
    // B-1 / V-4: SELF-COMPUTE — the document carries 0, which must NOT be used.
    // Default rate 19 % (§12 Abs. 1 UStG); user can override to 7 % in the modal.
    // Even §19 Kleinunternehmer owe this (§13b Abs. 8 UStG).
    // For a 381 Gutschrift netCents is already negative → vatOwedCents negative
    // automatically (§17 reverse-charge correction); branch unchanged.
    vatOwedCents = Math.round(netCents * 19 / 100);
  } else if (taxScheme === 'standard') {
    // For a 381 Gutschrift force the §17 Vorsteuer-Korrektur negative,
    // independent of the source sign (see Cent conversion above).
    vatOwedCents = isCredit
      ? -Math.abs(eurosToCents(parsed.taxTotal ?? 0))
      : eurosToCents(parsed.taxTotal ?? 0);
  } else {
    // import_third_country (V-3), small_business_19, zero_exempt, needs_review
    vatOwedCents = 0;
  }

  // ── §14 Completeness check ──────────────────────────────────────────────────
  const { completenessStatus, missingMandatoryFields } = checkInputInvoiceCompleteness(
    parsed,
    taxScheme,
  );

  // ── userIsFullyVatDeductible (V-4) ──────────────────────────────────────────
  // Fail-closed: when isSmallBusiness is undefined/null, treat as not fully deductible.
  const userIsFullyVatDeductible = sellerContext.isSmallBusiness === false;

  // ── vatDeductible derivation (C-3, fail-closed) ─────────────────────────────
  let vatDeductible = false;

  if (completenessStatus === 'complete') {
    if (taxScheme === 'standard' && vatOwedCents !== 0) {
      // §17 Abs. 1 UStG: a Gutschrift (vatOwedCents < 0) is a Vorsteuer-Korrektur
      // and MUST be deductible-eligible too. The negative sign flows through
      // vatDeductibleCents = vatDeductible ? vatOwedCents : 0 automatically below.
      vatDeductible = true;
    } else if (
      (taxScheme === 'reverse_charge_13b' || taxScheme === 'intra_community_6a') &&
      userIsFullyVatDeductible
    ) {
      vatDeductible = true;
    }
    // import_third_country, small_business_19, zero_exempt, needs_review → false
  }
  // incomplete or needs_review completeness → false (already initialised to false)

  // ── vatDeductibleCents ──────────────────────────────────────────────────────
  const vatDeductibleCents = vatDeductible ? vatOwedCents : 0;

  // ── Rounding difference ──────────────────────────────────────────────────────
  // For standard invoices, net + vat should equal gross. Surface any difference
  // rather than silently absorbing it (Beleg-Rundung).
  const roundingDiffCents =
    taxScheme === 'standard'
      ? grossCents - netCents - vatOwedCents
      : 0;

  // ── vatRate + vatBreakdown ───────────────────────────────────────────────────
  const breakdown = parsed.taxBreakdown ?? [];

  // F-2: For §13b/IGL the document carries taxRate=0 (the supplier charges nothing).
  // vatRate must reflect the SELF-COMPUTED rate (19 % default) used for vatOwedCents,
  // not the irrelevant 0-Beleg-rate — they would be inconsistent otherwise.
  // For all other schemes: derive from the single breakdown entry, or null for multi-rate.
  let vatRate: number | null;
  if (taxScheme === 'reverse_charge_13b' || taxScheme === 'intra_community_6a') {
    vatRate = 19; // Default self-computed rate (B-2); user may override to 7 % in M4b modal
  } else {
    vatRate = breakdown.length === 1 ? (breakdown[0].taxRate ?? null) : null;
  }

  const vatBreakdown: ExpenseVatBreakdownEntry[] = breakdown.map((b) => {
    // For a 381 Gutschrift force every per-line amount negative as well, so the
    // Σ per-line totals stay consistent with the negative document totals
    // (Frage 4 — source sign is ambiguous, always -Math.abs).
    const lineNetCents = isCredit
      ? -Math.abs(eurosToCents(b.taxBasisAmount ?? 0))
      : eurosToCents(b.taxBasisAmount ?? 0);
    let lineVatOwedCents: number;

    if (taxScheme === 'reverse_charge_13b' || taxScheme === 'intra_community_6a') {
      // Self-compute per line as well (negative lineNetCents → negative result).
      lineVatOwedCents = Math.round(lineNetCents * 19 / 100);
    } else if (taxScheme === 'standard') {
      lineVatOwedCents = isCredit
        ? -Math.abs(eurosToCents(b.taxAmount ?? 0))
        : eurosToCents(b.taxAmount ?? 0);
    } else {
      lineVatOwedCents = 0;
    }

    // Per-line deductible follows the same vatDeductible decision
    const lineVatDeductibleCents = vatDeductible ? lineVatOwedCents : 0;

    return {
      categoryCode: b.categoryCode ?? '',
      taxRate: b.taxRate ?? 0,
      netCents: lineNetCents,
      vatOwedCents: lineVatOwedCents,
      vatDeductibleCents: lineVatDeductibleCents,
    };
  });

  // ── serviceDate — §14 Nr. 6 (BT-72 preferred, BT-73 fallback) ──────────────
  const serviceDate =
    nonEmpty(parsed.deliveryDate ?? undefined)
      ? (parsed.deliveryDate ?? null)
      : nonEmpty(parsed.billingPeriodStart ?? undefined)
        ? (parsed.billingPeriodStart ?? null)
        : null;

  // ── Non-EUR currency flag ───────────────────────────────────────────────────
  const nonEurCurrency =
    nonEmpty(parsed.currency) && parsed.currency.toUpperCase() !== 'EUR'
      ? parsed.currency
      : null;

  return {
    supplierName: parsed.sellerName ?? '',
    supplierVatId: nonEmpty(parsed.sellerVatId) ? parsed.sellerVatId : null,
    supplierTaxId: nonEmpty(parsed.sellerTaxId) ? parsed.sellerTaxId : null,
    invoiceNumber: nonEmpty(parsed.documentNo) ? parsed.documentNo : null,
    documentTypeCode: parsed.documentTypeCode ?? '',

    netCents,
    vatOwedCents,
    vatDeductibleCents,
    grossCents,
    vatRate,
    vatBreakdown,
    roundingDiffCents,

    taxScheme,
    taxSchemeConfirmedByUser: false,
    vatDeductible,

    completenessStatus,
    missingMandatoryFields,

    expenseDate: parsed.documentDate ?? '',
    dueDate: nonEmpty(parsed.dueDate ?? undefined) ? parsed.dueDate : null,
    serviceDate,

    category: null,
    skrAccount: null,
    sourceReceivedInvoiceId: null,
    nonEurCurrency,

    status: 'draft',
  };
}
